/**
 * The end date, between a timestamp and the two controls that edit it.
 *
 * A date field and a time list rather than the `datetime-local` this replaces,
 * for one reason: `datetime-local` offers every minute there is, and the
 * product's rule is that an end date lands on the hour or the half hour. A
 * `step` attribute would be a hint a browser is free to ignore; a list of
 * forty-eight options is the rule.
 *
 * The conversion is local on purpose. A tutor typing 23:30 means half past
 * eleven where they are, so the parts go through the `Date` constructor, which
 * reads them in the browser's own zone, and come back out the same way. What
 * is stored and sent is the instant.
 */
import { formatTime, type Language } from '@athena/i18n'

/** Every half hour of the day, as a `HH:mm` the time list is keyed by. */
export const HALF_HOUR_TIMES: readonly string[] = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2)
  const minutes = index % 2 === 0 ? '00' : '30'
  return `${String(hours).padStart(2, '0')}:${minutes}`
})

/**
 * A `HH:mm` from the list, as the page's language writes a time of day:
 * "3:30 p. m." on a Spanish page and "3:30 PM" on an English one.
 *
 * The value stays `HH:mm` — it is what the form holds and converts — and only
 * the label is localized. A day that exists in every zone carries it, so no
 * daylight-saving gap can turn 02:30 into 03:30.
 */
export function timeLabel(time: string, language: Language): string {
  const [hours, minutes] = time.split(':').map(Number)
  return formatTime(new Date(2000, 0, 15, hours ?? 0, minutes ?? 0), language)
}

/** What the form holds: a `YYYY-MM-DD` and a `HH:mm`, either of which may be blank. */
export interface AvailabilityParts {
  date: string
  time: string
}

export const NO_AVAILABILITY: AvailabilityParts = { date: '', time: '' }

/**
 * An instant, as the two fields.
 *
 * The time is rounded **down** to the half hour it falls in rather than
 * refused. A practice whose end date predates this control has an arbitrary
 * minute on it, and a form that showed a blank time for it would quietly
 * propose removing the end date the next time anybody pressed Save.
 */
export function toAvailabilityParts(iso: string | null): AvailabilityParts {
  if (iso === null) return NO_AVAILABILITY

  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return NO_AVAILABILITY

  const date = [
    String(when.getFullYear()).padStart(4, '0'),
    String(when.getMonth() + 1).padStart(2, '0'),
    String(when.getDate()).padStart(2, '0'),
  ].join('-')
  const hours = String(when.getHours()).padStart(2, '0')
  const minutes = when.getMinutes() < 30 ? '00' : '30'

  return { date, time: `${hours}:${minutes}` }
}

/** Why an end date cannot be built from what is on the form. */
export type AvailabilityProblem = 'MISSING_DATE' | 'MISSING_TIME'

export type AvailabilityResult =
  { ok: true; value: string | null } | { ok: false; problem: AvailabilityProblem }

/**
 * The two fields, as the instant to send, or null for no end date.
 *
 * Both blank means no end date, which is the permissive value and what a
 * practice starts with. One of the two blank is not a third meaning — it is a
 * half-filled form, and saying so beats guessing midnight or today.
 */
export function toAvailabilityInstant({ date, time }: AvailabilityParts): AvailabilityResult {
  const hasDate = date.trim() !== ''
  const hasTime = time.trim() !== ''

  if (!hasDate && !hasTime) return { ok: true, value: null }
  if (!hasDate) return { ok: false, problem: 'MISSING_DATE' }
  if (!hasTime) return { ok: false, problem: 'MISSING_TIME' }

  const [year, month, day] = date.split('-').map(Number)
  const [hours, minutes] = time.split(':').map(Number)
  if ([year, month, day, hours, minutes].some((part) => part === undefined || Number.isNaN(part))) {
    return { ok: false, problem: 'MISSING_DATE' }
  }

  // Local parts in, instant out. Seconds and milliseconds are zero, which is
  // half of what the server means by "on the half hour".
  const when = new Date(year!, month! - 1, day!, hours!, minutes!, 0, 0)
  if (Number.isNaN(when.getTime())) return { ok: false, problem: 'MISSING_DATE' }

  return { ok: true, value: when.toISOString() }
}
